import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineSpinner } from '@/components/ui/inline-spinner';
import { cn } from '@/lib/utils';
import { useOverrideBeginningBalance } from '@/hooks/queries/useFinanceReports';
import { formatAmount } from '../utils';

interface BeginningBalanceEditorProps {
  reportId: string;
  beginningBalance: number;
  concurrencyVersion: number;
  /**
   * FCG-009 — only a pre-filing (Ready/Overdue) report's beginning balance is
   * editable; a Filed/Amended report's figure is frozen in the as-filed
   * snapshot, so the parent renders it read-only.
   */
  editable: boolean;
}

/**
 * FCG-009 — inline edit affordance for the beginning balance. It defaults to the
 * prior period's as-filed ending cash; the treasurer can correct it here before
 * filing. The override is audit-logged on the backend.
 */
export function BeginningBalanceEditor({
  reportId,
  beginningBalance,
  concurrencyVersion,
  editable,
}: BeginningBalanceEditorProps) {
  const { overrideBeginningBalance } = useOverrideBeginningBalance(reportId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(beginningBalance));
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSave = async () => {
    setErrorMessage(null);
    const parsed = Number(draft);
    if (draft.trim() === '' || Number.isNaN(parsed)) {
      setErrorMessage('Enter a dollar amount.');
      return;
    }
    setSaving(true);
    try {
      await overrideBeginningBalance({
        reportId,
        data: { beginningBalance: parsed, concurrencyVersion },
      });
      setEditing(false);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Failed to update beginning balance');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(String(beginningBalance));
    setErrorMessage(null);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <p className="text-lg font-semibold" data-testid="beginning-balance-display">
          {formatAmount(beginningBalance)}
        </p>
        {editable && (
          <button
            type="button"
            onClick={() => {
              // Re-seed the draft from the current value so a re-open after a
              // save (or a concurrent change) shows the live figure, not a
              // stale typed value.
              setDraft(String(beginningBalance));
              setErrorMessage(null);
              setEditing(true);
            }}
            data-testid="beginning-balance-edit-button"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="beginning-balance-editor">
      <div className="flex items-center gap-2">
        <label htmlFor="beginning-balance-input" className="sr-only">
          Beginning balance
        </label>
        <input
          id="beginning-balance-input"
          data-testid="beginning-balance-input"
          type="number"
          step="0.01"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className={cn(
            'w-40 rounded-md border border-input bg-background px-3 py-2 text-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          )}
        />
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="beginning-balance-save-button">
          {saving ? <InlineSpinner label="Saving" className="mr-1 h-3.5 w-3.5" /> : <Check className="mr-1 h-3.5 w-3.5" />}
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving}>
          <X className="mr-1 h-3.5 w-3.5" />
          Cancel
        </Button>
      </div>
      {errorMessage && (
        <span className="text-sm text-destructive" data-testid="beginning-balance-error">
          {errorMessage}
        </span>
      )}
      <p className="text-xs text-muted-foreground">
        Defaults to the prior period's ending cash. Adjust only if it doesn't match your records.
      </p>
    </div>
  );
}
